import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  TextField,
  useMediaQuery,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import { Formik } from "formik";
import * as yup from "yup";
import Swal from "sweetalert2";
import Header from "../../../../components/Header";
import { tokens } from "../../../../theme";
import { useTheme } from "@mui/material";
import { useDispatch } from "react-redux";
import { UpdateDataToStore } from "../../../../store/Actions/CustomAction";
import { generateAndExecuteMutation, graphqlQuery } from "../../../../data/Axios/DynamicService";
import { allUsersQuery } from "../../../../data/Axios/queries";
import { caseManagementUrl, userManagementUrl } from "../../../../config";

const TimeTrackerForm = (props) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const dispatch = useDispatch();
  const isNonMobile = useMediaQuery("(min-width:600px)");
  const base_url = caseManagementUrl.uri;
  const userbase_url = userManagementUrl.uri;

  const caseIdFromSession = JSON.parse(localStorage.getItem("CaseId"));

  // ✅ Local state for users
  const [userList, setUserList] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);

  // Fetch all users/advocates
  useEffect(() => {
    async function fetchUsers() {
      try {
        const allUsersData = await graphqlQuery(allUsersQuery, userbase_url);
        if (allUsersData) {
          setUserList(allUsersData);
          console.log("Users loaded:", allUsersData);
        }
      } catch (error) {
        console.error("Error fetching advocates/users:", error);
      } finally {
        setLoadingUsers(false);
      }
    }
    fetchUsers();
  }, []);

  // Set initial values based on whether we're editing or creating
  const getInitialValues = () => {
    if (props.isEditing && props.data) {
      return {
        userAssignedId: props.data.userAssignedId?.toString() || "",
        billableItem: props.data.billableItem || "",
        description: props.data.description || "",
        currency: props.data.currency || 1,
        travelHours: props.data.travelHours || "",
        otherHours: props.data.otherHours || "",
        ratePerHour: props.data.ratePerHour || "",
        totalBillableHours: props.data.totalBillableHours || "",
        status: props.data.status || 1,
      };
    }
    
    return {
      userAssignedId: "",
      billableItem: "",
      description: "",
      currency: 1,
      travelHours: "",
      otherHours: "",
      ratePerHour: "",
      totalBillableHours: "",
      status: 1,
    };
  };

  const validationSchema = yup.object().shape({
    userAssignedId: yup.string().required("User is required"),
    billableItem: yup.string().required("Billable item is required"),
    description: yup.string().required("Description is required"),
    travelHours: yup.number().min(0, "Must be 0 or more").required("Travel hours required"),
    otherHours: yup.number().min(0, "Must be 0 or more").required("Other hours required"),
    ratePerHour: yup.number().min(0, "Must be 0 or more").required("Rate per hour required"),
  });

  // Function to calculate total billable hours
  const calculateTotalHours = (travelHours, otherHours) => {
    const travel = parseFloat(travelHours) || 0;
    const other = parseFloat(otherHours) || 0;
    return travel + other;
  };

const handleSubmit = async (values, { setSubmitting, resetForm }) => {
  try {
    // Calculate total billable hours
    const totalBillableHours = calculateTotalHours(values.travelHours, values.otherHours);

    // Prepare mutation data
    const mutationData = {
      userAssignedId: parseInt(values.userAssignedId),
      caseId: caseIdFromSession,
      billableItem: values.billableItem.trim(),
      description: values.description.trim(),
      currency: 1,
      travelHours: parseFloat(values.travelHours) || 0,
      otherHours: parseFloat(values.otherHours) || 0,
      totalBillableHours: totalBillableHours,
      ratePerHour: parseFloat(values.ratePerHour),
      exchangeRate: null,
      status: 1,
    };

    if (props.isEditing && props.data?.id) {
      mutationData.id = props.data.id;
    }

    console.log("Mutation data:", mutationData);

    const response = await generateAndExecuteMutation(
      props.isEditing ? "updateTimeTracker" : "createTimeTracker",
      mutationData,
      "timeTrackers",
      base_url
    );

    const mutationName = props.isEditing ? "updateTimeTracker" : "createTimeTracker";
    const responseData = response?.data?.[mutationName] || response?.[mutationName];
    
    if (responseData?.id) {
      props.onClose?.();
      props.onAction?.(); // This should refresh the parent component

      Swal.fire({
        icon: "success",
        title: "Success!",
        text: props.isEditing
          ? "Time tracker updated successfully."
          : "Time tracker created successfully.",
      });

      resetForm();
    } else {
      console.error("Unexpected response structure:", response);
      throw new Error(
        responseData?.message || 
        "Server returned unexpected response format"
      );
    }
  } catch (error) {
    console.error("Submission error:", error);
    Swal.fire({
      icon: "error",
      title: "Error!",
      text: error.message || "An error occurred while saving the time tracker. Please try again.",
    });
  } finally {
    setSubmitting(false);
  }
};

  return (
    <Box m="20px">
      <Header
        title={props.isEditing ? "Edit Time Tracker" : "Create Time Tracker"}
        subtitle={
          props.isEditing
            ? "Update recorded time for this case activity"
            : "Record time spent on a specific case activity"
        }
      />

      <Formik
        onSubmit={handleSubmit}
        initialValues={getInitialValues()}
        validationSchema={validationSchema}
        enableReinitialize
      >
        {({
          values,
          errors,
          touched,
          handleBlur,
          handleChange,
          handleSubmit,
          isSubmitting,
          setFieldValue,
        }) => (
          <form onSubmit={handleSubmit}>
            <Box
              display="grid"
              gap="20px"
              gridTemplateColumns="repeat(4, minmax(0, 1fr))"
              sx={{
                "& > div": { gridColumn: isNonMobile ? undefined : "span 4" },
              }}
            >
              {/* ✅ User Dropdown */}
              <TextField
                select
                fullWidth
                variant="filled"
                label="Assign To"
                name="userAssignedId"
                value={values.userAssignedId}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.userAssignedId && Boolean(errors.userAssignedId)}
                helperText={touched.userAssignedId && errors.userAssignedId}
                sx={{ gridColumn: "span 2" }}
              >
                {loadingUsers ? (
                  <MenuItem disabled>
                    <CircularProgress size={20} />
                    &nbsp;Loading...
                  </MenuItem>
                ) : userList.length === 0 ? (
                  <MenuItem disabled>No active users found</MenuItem>
                ) : (
                  userList.map((user) => (
                    <MenuItem key={user.id} value={user.id}>
                      {user.firstName} {user.lastName || ""} ({user.username})
                    </MenuItem>
                  ))
                )}
              </TextField>

              <TextField
                fullWidth
                variant="filled"
                label="Billable Item *"
                name="billableItem"
                value={values.billableItem}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.billableItem && Boolean(errors.billableItem)}
                helperText={touched.billableItem && errors.billableItem}
                sx={{ gridColumn: "span 2" }}
              />

              <TextField
                fullWidth
                variant="filled"
                label="Description *"
                name="description"
                multiline
                rows={3}
                value={values.description}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.description && Boolean(errors.description)}
                helperText={touched.description && errors.description}
                sx={{ gridColumn: "span 4" }}
              />

              {/* ✅ Currency Dropdown (KES only) */}
              <TextField
                select
                fullWidth
                variant="filled"
                label="Currency"
                name="currency"
                value={values.currency}
                disabled
                sx={{ gridColumn: "span 2" }}
              >
                <MenuItem value={1}>KES</MenuItem>
              </TextField>

              <TextField
                fullWidth
                variant="filled"
                label="Travel Hours *"
                name="travelHours"
                type="number"
                inputProps={{ min: 0, step: 0.5 }}
                value={values.travelHours}
                onChange={(e) => {
                  handleChange(e);
                  // Auto-calculate total when travel hours change
                  const total = calculateTotalHours(e.target.value, values.otherHours);
                  setFieldValue("totalBillableHours", total);
                }}
                onBlur={handleBlur}
                error={touched.travelHours && Boolean(errors.travelHours)}
                helperText={touched.travelHours && errors.travelHours}
                sx={{ gridColumn: "span 2" }}
              />

              <TextField
                fullWidth
                variant="filled"
                label="Other Hours *"
                name="otherHours"
                type="number"
                inputProps={{ min: 0, step: 0.5 }}
                value={values.otherHours}
                onChange={(e) => {
                  handleChange(e);
                  // Auto-calculate total when other hours change
                  const total = calculateTotalHours(values.travelHours, e.target.value);
                  setFieldValue("totalBillableHours", total);
                }}
                onBlur={handleBlur}
                error={touched.otherHours && Boolean(errors.otherHours)}
                helperText={touched.otherHours && errors.otherHours}
                sx={{ gridColumn: "span 2" }}
              />

              <TextField
                fullWidth
                variant="filled"
                label="Rate per Hour *"
                name="ratePerHour"
                type="number"
                inputProps={{ min: 0, step: 0.01 }}
                value={values.ratePerHour}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.ratePerHour && Boolean(errors.ratePerHour)}
                helperText={touched.ratePerHour && errors.ratePerHour}
                sx={{ gridColumn: "span 2" }}
              />

              <TextField
                fullWidth
                variant="filled"
                label="Total Billable Hours"
                name="totalBillableHours"
                type="number"
                value={calculateTotalHours(values.travelHours, values.otherHours)}
                disabled
                sx={{ gridColumn: "span 2" }}
                helperText="Automatically calculated (Travel + Other Hours)"
              />

              {/* Optional: Show total cost calculation */}
              <TextField
                fullWidth
                variant="filled"
                label="Total Cost"
                value={
                  values.ratePerHour && values.travelHours && values.otherHours
                    ? `KES ${(
                        calculateTotalHours(values.travelHours, values.otherHours) * 
                        parseFloat(values.ratePerHour || 0)
                      ).toFixed(2)}`
                    : "KES 0.00"
                }
                disabled
                sx={{ gridColumn: "span 2" }}
                helperText="Total Hours × Rate per Hour"
              />
            </Box>

            <Box display="flex" justifyContent="end" mt="20px" gap="10px">
              <Button
                variant="outlined"
                onClick={props.onClose}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                color="secondary"
                variant="contained"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Submitting..." : "Submit"}
              </Button>
            </Box>
          </form>
        )}
      </Formik>
    </Box>
  );
};

export default TimeTrackerForm;